import { apiFetch } from '../services/cloudflareService';
import React, { useState, useEffect } from 'react';
import { Hospital, AdAsset, AdSettings, AdType, AdTarget, DEFAULT_AD_SETTINGS, AD_IMAGE_SPECS, parseYouTubeUrl, ScreenSession } from '../types';
import {
    Megaphone, Plus, Trash2, Image, Type, ScrollText, Monitor, Smartphone, Eye, EyeOff, ChevronDown, ChevronUp, Upload, X, Tv, Info, PlayCircle, Clock, CalendarClock, Check, Pencil, ExternalLink, Gauge
} from 'lucide-react';
import AdManagerSettings from './AdManagerSettings';
import AdManagerPreview from './AdManagerPreview';
import { AdWizard } from './AdWizard';

const AD_TYPE_OPTIONS: { value: AdType; label: string; icon: React.ElementType; description: string }[] = [
    { value: 'BANNER_TEXT', label: 'Banner de Texto', icon: Type, description: 'Texto con emoji/icono' },
    { value: 'BANNER_IMAGE', label: 'Banner Imagen', icon: Image, description: 'Imagen horizontal (TV/móvil)' },
    { value: 'TICKER', label: 'Ticker', icon: ScrollText, description: 'Mensaje corriendo abajo' },
    { value: 'FULLSCREEN_SLIDE', label: 'Slide Fullscreen', icon: Monitor, description: 'Imagen o video (TV vacía)' },
];

const AD_TARGET_OPTIONS: { value: AdTarget; label: string; icon: React.ElementType }[] = [
    { value: 'ALL', label: 'Todos', icon: Eye },
    { value: 'TV_ONLY', label: 'Solo TV', icon: Tv },
    { value: 'MOBILE_ONLY', label: 'Solo Celular', icon: Smartphone },
];

interface AdManagerProps {
    hospital: Hospital;
    onUpdate: (updatedHospital: Hospital) => void;
    screens?: ScreenSession[];
    globalMode?: boolean;
    groupId?: string;
    globalAssets?: AdAsset[];
    onUpdateGlobalAssets?: (assets: AdAsset[]) => void;
    globalSettings?: AdSettings;
    onUpdateGlobalSettings?: (settings: AdSettings) => void;
}

const AdManager: React.FC<AdManagerProps> = ({ hospital, onUpdate, screens = [], globalMode, groupId, globalAssets, onUpdateGlobalAssets, globalSettings, onUpdateGlobalSettings }) => {
    const primaryColor = hospital.branding?.primaryColor || '#5B65DC';
    const [settings, setSettings] = useState<AdSettings>((globalMode && globalSettings) ? globalSettings : (hospital.preferences?.adSettings || DEFAULT_AD_SETTINGS));
    const [assets, setAssets] = useState<AdAsset[]>(globalMode ? (globalAssets || []) : (hospital.preferences?.adAssets || []));
    const [saving, setSaving] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
    const [showMobilePreview, setShowMobilePreview] = useState(false);
    
    // Wizard state
    const [showWizard, setShowWizard] = useState(false);
    const [wizardInitialData, setWizardInitialData] = useState<AdAsset | null>(null);
    const [liveDraft, setLiveDraft] = useState<AdAsset | null>(null);
    const [saveToast, setSaveToast] = useState(false);

    // Form
    const [newType, setNewType] = useState<AdType>('BANNER_TEXT');
    const [newName, setNewName] = useState('');  // Nombre/descripción para identificar el anuncio
    const [newText, setNewText] = useState('');
    const [newTextSize, setNewTextSize] = useState<'sm' | 'md' | 'lg' | 'xl'>('md');
    const [newTickerSpeed, setNewTickerSpeed] = useState<'slow' | 'normal' | 'fast'>(settings.tickerSpeed || 'normal');
    const [newIcon, setNewIcon] = useState('');
    const [newTarget, setNewTarget] = useState<AdTarget>('ALL');
    const [newDisplayTime, setNewDisplayTime] = useState(15);
    const [newFrequencyPerHour, setNewFrequencyPerHour] = useState<number>(0);
    const [newImageUrl, setNewImageUrl] = useState('');
    const [newImageAlt, setNewImageAlt] = useState('');
    const [newMediaType, setNewMediaType] = useState<'image' | 'video'>('image');
    const [newYoutubeUrl, setNewYoutubeUrl] = useState('');
    const [newScheduleEnabled, setNewScheduleEnabled] = useState(false);
    const [newScheduleDays, setNewScheduleDays] = useState<number[]>([1, 2, 3, 4, 5, 6, 0]);
    const [newScheduleMode, setNewScheduleMode] = useState<'range' | 'exact'>('range');
    const [newScheduleStart, setNewScheduleStart] = useState('08:00');
    const [newScheduleEnd, setNewScheduleEnd] = useState('20:00');
    const [newScheduleExactTime, setNewScheduleExactTime] = useState('08:00');
    const [newScheduleExactTimes, setNewScheduleExactTimes] = useState<string[]>([]);

    const DAYS_OF_WEEK = [
        { val: 1, label: 'L' }, { val: 2, label: 'M' }, { val: 3, label: 'M' },
        { val: 4, label: 'J' }, { val: 5, label: 'V' }, { val: 6, label: 'S' }, { val: 0, label: 'D' }
    ];

    useEffect(() => {
        setSettings((globalMode && globalSettings) ? globalSettings : (hospital.preferences?.adSettings || DEFAULT_AD_SETTINGS));
        setAssets(globalMode ? (globalAssets || []) : (hospital.preferences?.adAssets || []));
    }, [hospital, globalMode, globalAssets, globalSettings]);

    useEffect(() => {
        if (!showAddForm) setNewTickerSpeed(settings.tickerSpeed || 'normal');
    }, [settings.tickerSpeed, showAddForm]);

    const saveAll = async (newSettings: AdSettings, newAssets: AdAsset[]) => {
        setSaving(true);
        try {
            if (globalMode && groupId && onUpdateGlobalAssets) {
                // Save to group level
                const { updateGroupPreferences } = await import('../services/cloudflareService');
                const success = await updateGroupPreferences(groupId, { globalAdAssets: newAssets, globalAdSettings: newSettings });
                if (!success) throw new Error('Error guardando anuncios globales');
                setSettings(newSettings);
                setAssets(newAssets);
                onUpdateGlobalAssets(newAssets);
                if (onUpdateGlobalSettings) onUpdateGlobalSettings(newSettings);
            } else {
                // Save to hospital level
                const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
                const newPrefs = { ...hospital.preferences, adSettings: newSettings, adAssets: newAssets };
                const res = await apiFetch(`${baseUrl}/api/hospitals/${hospital.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        preferences: newPrefs,
                    }),
                });
                if (!res.ok) {
                    if (res.status === 401) {
                        alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                        window.location.href = '/login';
                        return;
                    }
                    throw new Error('Error guardando anuncios');
                }
                setSettings(newSettings);
                setAssets(newAssets);
                onUpdate({ ...hospital, preferences: newPrefs } as Hospital);
            }
            setSaveToast(true);
            setTimeout(() => setSaveToast(false), 2000);
        } catch (e) {
            console.error(e);
            alert('Error al guardar los anuncios');
        } finally {
            setSaving(false);
        }
    };

    const updateSetting = async (key: keyof AdSettings, value: any) => await saveAll({ ...settings, [key]: value }, assets);

    const toggleAssetActive = async (id: string) => {
        const updated = assets.map(a => a.id === id ? { ...a, isActive: !a.isActive } : a);
        await saveAll(settings, updated);
    };

    const deleteAsset = async (id: string) => {
        const asset = assets.find(a => a.id === id);
        if (asset?.imageUrl && asset.imageUrl.startsWith('/api/ad-media/')) {
            try {
                const key = asset.imageUrl.replace('/api/ad-media/', '');
                const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
                const res = await apiFetch(`${baseUrl}/api/ad-media`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ key }),
                });
                if (res.status === 401) {
                    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                    window.location.href = '/login';
                    return;
                }
            } catch (e) { console.error(e); }
        }
        await saveAll(settings, assets.filter(a => a.id !== id));
    };

    const moveAsset = async (id: string, dir: 'up' | 'down') => {
        const idx = assets.findIndex(a => a.id === id);
        if ((dir === 'up' && idx === 0) || (dir === 'down' && idx === assets.length - 1)) return;
        const newAssets = [...assets];
        const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
        [newAssets[idx], newAssets[swapIdx]] = [newAssets[swapIdx], newAssets[idx]];
        await saveAll(settings, newAssets.map((a, i) => ({ ...a, priority: i + 1 })));
    };

    const handleImageUpload = async (file: File) => {
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('hospitalId', hospital.id);
            formData.append('adType', newType);
            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
            const res = await apiFetch(`${baseUrl}/api/ad-media/upload`, { method: 'POST', credentials: 'include', body: formData });
            if (!res.ok) {
                if (res.status === 401) {
                    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                    window.location.href = '/login';
                    return;
                }
                const errData = await res.json().catch(() => null);
                const msg = errData?.error || 'Error desconocido al subir imagen.';
                alert(`❌ ${msg}`);
                setUploadingImage(false);
                return;
            }
            const data = await res.json();
            setNewImageUrl(data.imageUrl);
            setNewMediaType(data.mediaType || 'image');
        } catch {
            alert('❌ No se pudo conectar al servidor. Verifica tu conexión.');
        } finally { setUploadingImage(false); }
    };

    const resetForm = () => {
        setEditingAssetId(null);
        setNewName(''); setNewText(''); setNewTextSize('md'); setNewIcon(''); setNewTarget('ALL'); setNewDisplayTime(15); setNewImageUrl(''); setNewImageAlt('');
        setNewFrequencyPerHour(0); setNewMediaType('image'); setNewYoutubeUrl(''); setNewTickerSpeed(settings.tickerSpeed || 'normal');
        setNewScheduleEnabled(false); setNewScheduleDays([1, 2, 3, 4, 5, 6, 0]); setNewScheduleStart('08:00'); setNewScheduleEnd('20:00');
        setNewScheduleMode('range'); setNewScheduleExactTimes([]); setNewScheduleExactTime('08:00');
        setShowAddForm(false);
    };

    const handleEditAsset = (asset: AdAsset) => {
        setWizardInitialData(asset);
        setShowWizard(true);
    };

    const handleWizardSave = async (asset: AdAsset, newTickerSpeed?: 'slow' | 'normal' | 'fast') => {
        const isEdit = assets.some(a => a.id === asset.id);
        const newAssets = isEdit ? assets.map(a => a.id === asset.id ? asset : a) : [...assets, asset];
        
        if (newTickerSpeed && newTickerSpeed !== settings.tickerSpeed) {
            await saveAll({ ...settings, tickerSpeed: newTickerSpeed }, newAssets);
        } else {
            await saveAll(settings, newAssets);
        }
        setShowWizard(false);
        setWizardInitialData(null);
        setLiveDraft(null);
    };

    const addAsset = async () => {
        if (!newType.includes('IMAGE') && newType !== 'FULLSCREEN_SLIDE' && !newText.trim()) return alert('El texto es requerido.');
        if ((newType.includes('IMAGE') || newType === 'FULLSCREEN_SLIDE') && !newImageUrl && !newYoutubeUrl) return alert('Sube una imagen/video o pega un enlace de YouTube.');

        let assetPriority = assets.length + 1;
        let assetCreatedAt = new Date().toISOString();

        if (editingAssetId) {
            const existing = assets.find(a => a.id === editingAssetId);
            if (existing) {
                assetPriority = existing.priority;
                if (existing.createdAt) assetCreatedAt = existing.createdAt;
            }
        }

        const isTextType = newType === 'BANNER_TEXT' || newType === 'TICKER';
        const isMediaType = newType === 'BANNER_IMAGE' || newType === 'FULLSCREEN_SLIDE';

        const asset: AdAsset = {
            id: editingAssetId || `AD-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
            type: newType,
            // TEXT-only fields
            ...(isTextType ? {
                text: newText || undefined,
                textSize: newTextSize !== 'md' ? newTextSize : undefined,
                icon: (newType === 'BANNER_TEXT' ? newIcon : undefined) || undefined,
            } : {}),
            // MEDIA-only fields
            ...(isMediaType ? {
                imageUrl: newImageUrl || undefined,
                imageAlt: newName || newImageAlt || undefined,
                mediaType: newMediaType,
                youtubeUrl: newYoutubeUrl || undefined,
            } : {}),
            displayTimeSec: newDisplayTime, target: newTarget, isActive: true,
            priority: assetPriority, createdAt: assetCreatedAt,
            frequencyPerHour: newFrequencyPerHour > 0 ? newFrequencyPerHour : undefined,
            schedule: newScheduleEnabled ? {
                daysOfWeek: newScheduleDays,
                ...(newScheduleMode === 'range' ? { startTime: newScheduleStart, endTime: newScheduleEnd } : {}),
                ...(newScheduleMode === 'exact' ? { exactTimes: newScheduleExactTimes } : {})
            } : undefined
        };
        const newAssets = editingAssetId ? assets.map(a => a.id === editingAssetId ? asset : a) : [...assets, asset];

        if (newType === 'TICKER' && newTickerSpeed !== settings.tickerSpeed) {
            await saveAll({ ...settings, tickerSpeed: newTickerSpeed }, newAssets);
        } else {
            await saveAll(settings, newAssets);
        }

        resetForm();
    };

    const needsImage = newType === 'BANNER_IMAGE' || newType === 'FULLSCREEN_SLIDE';
    const specs = needsImage ? AD_IMAGE_SPECS[newType] : null;

    const draftAsset: AdAsset = {
        id: 'draft-asset-preview',
        type: newType,
        target: newTarget,
        imageUrl: newImageUrl || undefined,
        imageAlt: newImageAlt || undefined,
        mediaType: newMediaType,
        youtubeUrl: newYoutubeUrl || undefined,
        text: newText || undefined,
        textSize: newTextSize !== 'md' ? newTextSize : undefined,
        icon: newIcon || undefined,
        displayTimeSec: newDisplayTime,
        frequencyPerHour: newFrequencyPerHour > 0 ? newFrequencyPerHour : undefined,
        isActive: true,
        priority: 0,
        schedule: newScheduleEnabled ? {
            daysOfWeek: newScheduleDays,
            ...(newScheduleMode === 'range' ? { startTime: newScheduleStart, endTime: newScheduleEnd } : {}),
            ...(newScheduleMode === 'exact' ? { exactTimes: newScheduleExactTimes } : {})
        } : undefined
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Split View Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

                {/* LEFT COL: Config + List (3 Cols) */}
                <div className="lg:col-span-3 space-y-6">
                    {showWizard ? (
                        <AdWizard
                            hospital={hospital}
                            settings={settings}
                            initialData={wizardInitialData}
                            screens={screens}
                            onClose={() => { setShowWizard(false); setWizardInitialData(null); setLiveDraft(null); }}
                            onSave={handleWizardSave}
                            onDelete={deleteAsset}
                            globalMode={globalMode}
                            groupId={groupId}
                            onDraftChange={setLiveDraft}
                        />
                    ) : (
                        <>
                            {/* Header Integrado con el Master Toggle */}
                            {!globalMode && (
                        <div className="flex items-center justify-between p-5 rounded-2xl border transition-all"
                            style={{ backgroundColor: settings.adsEnabled ? `${primaryColor}08` : '#f8fafc', borderColor: settings.adsEnabled ? `${primaryColor}20` : '#e2e8f0' }}>
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${settings.adsEnabled ? '' : 'bg-slate-100'}`}
                                    style={settings.adsEnabled ? { backgroundColor: `${primaryColor}15` } : {}}>
                                    <Megaphone className="w-6 h-6" style={{ color: settings.adsEnabled ? primaryColor : '#94a3b8' }} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-black text-slate-800 tracking-tight">Sistema de Publicidad</h2>
                                    <p className="text-xs text-slate-500">{settings.adsEnabled ? 'Publicidad activa en las pantallas del hospital' : 'Publicidad inactiva — no se muestran anuncios'}</p>
                                </div>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                {saving && <span className="text-[10px] font-bold text-slate-400 animate-pulse">Guardando...</span>}
                                <button onClick={() => updateSetting('adsEnabled', !settings.adsEnabled)}
                                    className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none scale-105`}
                                    style={{ backgroundColor: settings.adsEnabled ? primaryColor : '#cbd5e1' }}
                                    role="switch" aria-checked={settings.adsEnabled}>
                                    <span className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-md ring-0 transition duration-300 ${settings.adsEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
                                </button>
                            </div>
                        </div>
                    )}

                    {(globalMode || settings.adsEnabled) && (
                        <>
                            {/* Ajustes Avanzados (Acordeón) */}
                            {!globalMode && <AdManagerSettings settings={settings} updateSetting={updateSetting} primaryColor={primaryColor} />}

                            <div className="flex items-center justify-between mt-8">
                                <div className="flex items-center gap-3">
                                    <h3 className="font-bold text-sm text-slate-800">Tus Anuncios ({assets.length})</h3>
                                    {!globalMode && (
                                        <a href={`/${hospital.handle}/board`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 transition-colors px-2.5 py-1 rounded-md border border-slate-200">
                                            <ExternalLink className="w-3 h-3" /> Abrir Monitor Real
                                        </a>
                                    )}
                                </div>
                                <button onClick={() => {
                                    setWizardInitialData(null);
                                    setShowWizard(true);
                                }}
                                    className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-xl transition-all hover:scale-105 active:scale-95"
                                    style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}>
                                    <Plus className="w-4 h-4" />
                                    Agregar
                                </button>
                            </div>

                            {/* Lista de Anuncios */}
                            {assets.length === 0 && (!globalAssets || globalAssets.length === 0) && !showAddForm ? (
                                <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-2xl">
                                    <Megaphone className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                                    <p className="text-sm font-bold text-slate-400">Sin anuncios</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Mostrar Anuncios Globales primero en modo Sucursal */}
                                    {!globalMode && globalAssets && globalAssets.map((asset) => (
                                        <div key={asset.id} className="bg-slate-50 rounded-xl border border-indigo-100/50 p-3 flex items-center gap-3 shadow-sm relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                                            <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                                                {asset.imageUrl ? <img src={asset.imageUrl} className="w-full h-full object-cover" /> : asset.icon ? <span>{asset.icon}</span> : <Type className="w-4 h-4 text-slate-300" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex gap-2 items-center">
                                                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1">
                                                        <Globe className="w-3 h-3" /> Campaña Global
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-600 truncate font-semibold mt-1">{asset.name || asset.text || asset.imageAlt || 'Sin nombre'}</p>
                                                {asset.schedule?.startDate && (
                                                    <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                                        <CalendarClock className="w-3 h-3" />
                                                        {asset.schedule.startDate}{asset.schedule.endDate ? ` → ${asset.schedule.endDate}` : ' → Sin límite'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center pr-2">
                                                <Eye className="w-4 h-4 text-indigo-400 opacity-50" title="Activo desde corporativo" />
                                            </div>
                                        </div>
                                    ))}

                                    {/* Lista de Anuncios Propios (Locales o Globales si estoy en el hub corporativo) */}
                                    {assets.sort((a, b) => a.priority - b.priority).map((asset, index) => (
                                        <div key={asset.id} className={`bg-white rounded-xl border p-3 flex items-center gap-3 transition-all group shadow-sm ${asset.isActive ? 'hover:border-slate-300' : 'opacity-60'}`}>
                                            <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => moveAsset(asset.id, 'up')} disabled={index === 0} className="text-slate-300 hover:text-slate-600"><ChevronUp className="w-4 h-4" /></button>
                                                <button onClick={() => moveAsset(asset.id, 'down')} disabled={index === assets.length - 1} className="text-slate-300 hover:text-slate-600"><ChevronDown className="w-4 h-4" /></button>
                                            </div>
                                            <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                                                {asset.imageUrl ? <img src={asset.imageUrl} className="w-full h-full object-cover" /> : asset.icon ? <span>{asset.icon}</span> : <Type className="w-4 h-4 text-slate-300" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex gap-2 items-center">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}>
                                                        {AD_TYPE_OPTIONS.find(o => o.value === asset.type)?.label}
                                                    </span>
                                                    {globalMode && (
                                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-1">
                                                            <Globe className="w-3 h-3" /> Global
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-700 truncate font-bold mt-1">{asset.name || asset.text || asset.imageAlt || 'Sin nombre'}</p>
                                                {asset.schedule?.startDate && (
                                                    <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                                        <CalendarClock className="w-3 h-3" />
                                                        {asset.schedule.startDate}{asset.schedule.endDate ? ` → ${asset.schedule.endDate}` : ' → Sin límite'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => handleEditAsset(asset)} className="p-2 text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors" title="Editar / Ver">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => toggleAssetActive(asset.id)} className="p-2 transition-colors hover:bg-slate-50 rounded-lg" title={asset.isActive ? "Desactivar" : "Activar"}>
                                                    {asset.isActive ? <Eye className="w-4 h-4" style={{ color: primaryColor }} /> : <EyeOff className="w-4 h-4 text-slate-300" />}
                                                </button>
                                                <button onClick={() => deleteAsset(asset.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Form Inline */}
                            {/* AdWizard is now rendered at the top level when active */}
                        </>
                    )}
                    </>
                )}
                </div>

                {/* RIGHT COL: Live Preview (2 Cols) — Desktop */}
                {(globalMode || settings.adsEnabled) && (
                    <div className="lg:col-span-2 hidden lg:block sticky top-8">
                        <AdManagerPreview
                            settings={settings}
                            assets={showWizard && liveDraft ? [...assets.filter(a => a.id !== wizardInitialData?.id), liveDraft] : assets}
                            hospital={hospital}
                            primaryColor={primaryColor}
                        />
                    </div>
                )}
            </div>

            {/* Mobile Preview — Floating Button + Bottom Sheet */}
            {
                (globalMode || settings.adsEnabled) && (
                    <>
                        <button
                            onClick={() => setShowMobilePreview(true)}
                            className="lg:hidden fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full text-white text-xs font-bold shadow-xl active:scale-95 transition-transform"
                            style={{ backgroundColor: primaryColor, boxShadow: `0 8px 30px ${primaryColor}50` }}
                        >
                            <PlayCircle className="w-4 h-4" />
                            Vista Previa
                        </button>

                        {showMobilePreview && (
                            <div className="lg:hidden fixed inset-0 z-[100] flex flex-col">
                                {/* Backdrop */}
                                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowMobilePreview(false)} />
                                {/* Sheet */}
                                <div className="relative mt-auto max-h-[85vh] overflow-y-auto rounded-t-[2rem] animate-fade-in">
                                    <div className="sticky top-0 z-10 bg-[#0a0c10] pt-4 pb-2 flex justify-center">
                                        <div className="w-10 h-1 rounded-full bg-white/20" />
                                    </div>
                                    <AdManagerPreview
                                        settings={settings}
                                        assets={showWizard && liveDraft ? [...assets.filter(a => a.id !== wizardInitialData?.id), liveDraft] : assets}
                                        hospital={hospital}
                                        primaryColor={primaryColor}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )
            }

            {/* Save Toast */}
            {
                saveToast && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-5 py-3 bg-slate-800 text-white rounded-full shadow-xl text-xs font-bold animate-fade-in">
                        <Check className="w-4 h-4 text-emerald-400" />
                        Guardado exitosamente
                    </div>
                )
            }
        </div >
    );
};

export default AdManager;
